import React from 'react'
import clsx from 'clsx'
import { groupBy } from 'lodash'
import { IconButton, useTheme } from '@mui/material'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { AddCircle } from 'iconsax-react'

import { CustomFieldTableRowProps } from './Row'
import Table from './Table'
import Cards from './Cards'

import { getRenderableRows } from './helpers'
import { getUpdatedRows } from './Row/helpers'
import { selectCustomFields } from '@SonarCX/slices/customFields'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

type CustomFieldTableProps = Omit<
  CustomFieldTableRowProps,
  'cells' | 'fields' | 'deleteRow'
> & {
  setMeta: React.Dispatch<React.SetStateAction<APIMeta[]>>
  metaRows: APIMeta[]
  setMetaRows: React.Dispatch<React.SetStateAction<APIMeta[]>>
  onValueChange?: () => void
}

const CustomFieldTable: React.FC<CustomFieldTableProps> = ({
  table,
  meta,
  metaRows,
  setMetaRows,
  canEdit,
  onValueChange,
  setMeta,
  onBlur,
  ...props
}) => {
  const theme = useTheme()
  const dispatch = useAppDispatch()

  const { customFields } = useAppSelector(selectCustomFields)

  const { isSmall } = useBreakpoint()
  const { setTitle, setSubtext, setAction, setV2 } = useAreYouSure()

  const fields = React.useMemo(
    () =>
      table.options
        ? customFields.filter(
            field =>
              field.field_type !== 'table' &&
              field.id ===
                (table.options ?? []).find(option => option === field.id)
          )
        : customFields.filter(
            field =>
              field.field_type !== 'table' &&
              field.id ===
                (table.config?.options ?? []).find(
                  option => option.val === field.id
                )?.val
          ),
    [customFields, table]
  )

  const rowsToRender = React.useMemo(
    () =>
      getRenderableRows({
        meta,
        metaRows,
        tableID: table.id,
        tableOptions:
          table.options ??
          (table.config?.options ?? []).map(option => option.val)
      }),
    [meta, metaRows, table]
  )

  const classes = useStyles()

  const showError = React.useCallback(
    (message: string, duration?: number) => {
      const err = {
        err: {
          message
        }
      }

      dispatch(AddErrorAlert(err, duration))
    },
    [dispatch]
  )

  const handleOnAddRowClick = React.useCallback(() => {
    const id = table.id
    const rowPosition = rowsToRender.length
    const tableOptions = customFields.find(field => field.id === id)

    if (rowPosition > 0) {
      const previousRow = meta.filter(
        m =>
          m.key === id &&
          m.row === rowPosition - 1 &&
          (tableOptions.options
            ? tableOptions.options.some(opt => opt === m.row_key)
            : (tableOptions.config?.options ?? []).some(
                opt => opt.val === m.row_key
              ))
      )

      // Prevent adding empty rows, for now. this may be changed in future versions
      if (previousRow.every(row => row.value === '')) {
        showError(
          'Last row cannot be empty, add values before inserting a new one',
          4000
        )
        return
      }
    }

    if (rowPosition > 24) {
      showError('Only 25 rows are allowed')
      return
    }

    const newRow = fields.reduce((acc, curr) => {
      const newCell: APIMeta = {
        key: id,
        value: '',
        row: rowPosition,
        row_key: curr.id
      }

      acc.push(newCell)

      return acc
    }, [] as APIMeta[])

    setMetaRows(old => [...old, ...newRow])
  }, [
    table.id,
    rowsToRender.length,
    customFields,
    meta,
    showError,
    fields,
    setMetaRows
  ])

  const deleteRow = React.useCallback(
    (tableId: string, rowPosition: number) => {
      if (!canEdit) {
        return
      }

      const rowToDelete = metaRows.find(
        x => x.key === tableId && rowPosition === x.row
      )

      if (rowToDelete) {
        setMetaRows(old => old.filter(o => o.key !== tableId))
        return
      }

      const action = () => {
        onValueChange && onValueChange()

        setMeta(old => {
          const cleanedRows = old.filter(m => m.key !== tableId)
          const tableOptions = customFields.find(field => field.id === tableId)
          const toGroup = old.filter(
            m =>
              m.key === tableId &&
              (tableOptions.options
                ? tableOptions.options.some(opt => opt === m.row_key)
                : (tableOptions.config?.options ?? []).some(
                    opt => opt.val === m.row_key
                  ))
          )
          const grouped = groupBy(toGroup, c => c.row)
          const totalRows = Object.keys(grouped).length
          const sortedMeta: APIMeta[] = []

          for (const [key, values] of Object.entries(grouped)) {
            const currentRow = Number(key)
            const rows = getUpdatedRows({
              currentRow,
              rowPosition,
              options:
                tableOptions.options ??
                (tableOptions.config?.options ?? []).map(option => option.val),
              tableId,
              totalRows,
              //@ts-ignore
              values
            })
            sortedMeta.push(...rows)
          }

          return [...cleanedRows, ...sortedMeta]
        })

        onBlur()
      }

      setTitle('Delete row?')
      setSubtext('Continuing will erase that data')
      setAction(action)
      setV2(true)
    },
    [
      canEdit,
      customFields,
      metaRows,
      setMetaRows,
      onValueChange,
      onBlur,
      setMeta,
      setV2,
      setTitle,
      setSubtext,
      setAction
    ]
  )

  const DisplayComponent = React.useMemo(
    () => (isSmall ? Cards : Table),
    [isSmall]
  )

  return (
    <div>
      <div className={clsx(classes.title, 'cf-title')}>
        <span>{table.title}</span>

        {table.description && <span>{table.description}</span>}
      </div>

      <DisplayComponent
        {...{
          fields,
          rowsToRender,
          table,
          meta,
          metaRows,
          setMetaRows,
          canEdit,
          onBlur,
          deleteRow,
          ...props
        }}
      />

      {canEdit && (
        <div style={{ marginTop: '0.5rem' }}>
          <IconButton
            color='primary'
            className={classes.btn}
            onClick={handleOnAddRowClick}
          >
            <AddCircle size={theme.size.base} />
          </IconButton>
        </div>
      )}
    </div>
  )
}

export default CustomFieldTable
